import React from "react";

/**
 * Top-level error boundary so a single render crash anywhere in the tree
 * does NOT blank out the whole app. Shows a calm dossier-styled fallback
 * with a reload button.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("App crash caught by ErrorBoundary:", error, info?.componentStack);
  }

  hardReload = () => {
    try {
      // Clear caches that might be serving a stale bundle
      if ("caches" in window) {
        caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
      }
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistrations().then((rs) =>
          rs.forEach((r) => r.unregister())
        );
      }
    } catch (_) { /* noop */ }
    window.location.reload(true);
  };

  goSignIn = () => {
    window.location.href = "/signin";
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-[hsl(var(--background))]">
        <div className="max-w-md w-full hud-frame p-8">
          <div className="kpi-label">System notice</div>
          <h1 className="display text-2xl font-bold uppercase tracking-tight mt-3 leading-tight">
            Connection<span className="text-oud">_</span>blip.
          </h1>
          <div className="hairline-oud w-16 mt-4" />
          <p className="text-sm text-muted-foreground mt-5 leading-relaxed">
            Your session needs a quick refresh. This usually clears with a hard
            reload — no data is lost.
          </p>
          <div className="flex flex-col gap-2 mt-6">
            <button
              onClick={this.hardReload}
              className="hud-btn hud-btn-primary justify-center"
              data-testid="errboundary-reload"
            >
              HARD RELOAD
            </button>
            <button
              onClick={this.goSignIn}
              className="hud-btn justify-center"
              data-testid="errboundary-signin"
            >
              GO TO SIGN IN
            </button>
          </div>
          <details className="mt-6 mono text-[10px] uppercase tracking-widest text-muted-foreground/70">
            <summary className="cursor-pointer">Technical details</summary>
            <pre className="mt-2 text-[10px] normal-case tracking-normal whitespace-pre-wrap break-all opacity-60">
              {String(this.state.error?.message || this.state.error)}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
